import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    CurrentUserDocument,
    CurrentUserGroupsDocument,
    IOfferEventDetailFragment,
    IOfferEventSubscriptionStatus,
} from 'graphql/types';
import { IGroupConditionRow } from 'offer/types';

interface IUseGroupConditionRowsOptions {
    canManageParticipants?: boolean;
    offerEvent: IOfferEventDetailFragment;
}

export const useGroupConditionRows = ({
    canManageParticipants,
    offerEvent,
}: IUseGroupConditionRowsOptions) => {
    const {
        currentUserEnrollment,
        groupEnrollmentCounts,
        groupManagersCanEnrollParticipants,
        offer,
    } = offerEvent;

    // Skip the global user lookup when the event hasn't opted into manager
    // enrollment — `isGroupManager` is only consulted by that flow.
    const { data: currentUserData, loading: currentUserLoading } = useQuery(
        CurrentUserDocument,
        { skip: !groupManagersCanEnrollParticipants }
    );
    const isGroupManager = !!currentUserData?.currentUser?.isGroupManager;

    // Admins with `canManageParticipants` already get the richer enrollment
    // UI, so the group-manager variant is hidden for them.
    const canEnrollParticipantsAsGroupManager =
        !!groupManagersCanEnrollParticipants &&
        !canManageParticipants &&
        isGroupManager;

    // Skip the membership lookup when the event has no group conditions at
    // all — there is nothing for it to resolve and we don't want consumers
    // gating on a loading flag that never matters.
    const hasPotentialRows =
        !!groupEnrollmentCounts?.length || !!offer.groupConditions?.length;

    const { data: currentUserGroupsData, loading: currentUserGroupsLoading } =
        useQuery(CurrentUserGroupsDocument, {
            skip: !hasPotentialRows,
        });

    const { participantGroups, managerGroups } =
        currentUserGroupsData?.currentUser || {};

    const participantGroupIds = useMemo(
        () => new Set(participantGroups?.map((group) => group.id) || []),
        [participantGroups]
    );

    const managerGroupIds = useMemo(
        () => new Set(managerGroups?.map((group) => group.id) || []),
        [managerGroups]
    );

    // The user cannot subscribe again to a group if they already have a
    // non-Denied enrollment on the event.
    const alreadyEnrolled =
        !!currentUserEnrollment &&
        currentUserEnrollment.status !== IOfferEventSubscriptionStatus.Denied;

    const rows = useMemo(() => {
        const seenGroupIds = new Set<string>();

        const buildRow = (
            group: { id: string; name: string },
            min: number | null | undefined,
            max: number | null | undefined,
            count: number | null | undefined
        ): IGroupConditionRow | undefined => {
            if (seenGroupIds.has(group.id)) return undefined;

            const isParticipant = participantGroupIds.has(group.id);
            const isGroupManagerOfThisGroup = managerGroupIds.has(group.id);
            const canSubscribe = isParticipant && !alreadyEnrolled;
            const canEnrollParticipants =
                canEnrollParticipantsAsGroupManager &&
                isGroupManagerOfThisGroup;

            if (!canSubscribe && !canEnrollParticipants) return undefined;

            seenGroupIds.add(group.id);

            return {
                canEnrollParticipants,
                canSubscribe,
                count: count ?? undefined,
                group,
                max: max ?? undefined,
                min: min ?? undefined,
            };
        };

        const rows: IGroupConditionRow[] = [];

        for (const enrollmentCount of groupEnrollmentCounts || []) {
            const row = buildRow(
                enrollmentCount.group,
                enrollmentCount.min,
                enrollmentCount.max,
                enrollmentCount.count
            );

            if (row) rows.push(row);
        }

        // Also show groups from the offer itself, so groups without enrollment
        // counts still appear. We check both `minMaxFilters` (groups with
        // limits) and `groups` (everything in the condition) to catch
        // no-limit groups too.
        for (const groupCondition of offer.groupConditions || []) {
            for (const filter of groupCondition.minMaxFilters) {
                if (!filter.group) continue;

                const row = buildRow(
                    filter.group,
                    filter.min,
                    filter.max,
                    undefined
                );

                if (row) rows.push(row);
            }

            for (const edge of groupCondition.groups.edges) {
                if (!edge?.node) continue;

                const row = buildRow(edge.node, null, null, undefined);

                if (row) rows.push(row);
            }
        }

        return rows;
    }, [
        alreadyEnrolled,
        canEnrollParticipantsAsGroupManager,
        groupEnrollmentCounts,
        managerGroupIds,
        offer.groupConditions,
        participantGroupIds,
    ]);

    return {
        rows,
        loading: currentUserLoading || currentUserGroupsLoading,
        canEnrollParticipantsAsGroupManager,
    };
};
